import { FC, Fragment, useCallback } from 'react';
import { Layer, Marker, Source, type MarkerDragEvent } from 'react-map-gl';
import { useAtomValue } from 'jotai';
import { LTD_SLOT_SELECTED_LINE_PAINT } from 'components/map/ltd-slot-paint';
import { selectedLtdSlotIdsAtom } from './atoms';
import { useLtdLaterals, type LtdLateral } from './use-ltd-lateral';
import {
  lateralLengthFt,
  projectCursorToLengthFromStart,
  sliceLateralByLength,
  MIN_LTD_LEN_FT,
} from './ltd-lateral-trim';
import style from './unit-builder-modal.module.scss';

/**
 * On-map trim editor for the selected valued LTD laterals (one or several).
 *
 * Draws each selected lateral as a thicker, darker-green dashed line (the base
 * LTD line's colour in a darker tone) with a white circle handle at each end.
 * Dragging a handle moves ONLY that end to the cursor and holds the other end
 * where it is (no snap to the full-length end / the unit edge); for a multi-lateral
 * selection the same feet-moved delta is applied off that end to each. The new
 * coords go to the shared overlay the gun barrel rail's "Lateral length" field
 * reads and writes, so the two stay in sync. Right-click a handle to restore the
 * scoped laterals to full.
 *
 * Reads the selection from `selectedLtdSlotIdsAtom` (published by the gun barrel
 * pane) and the geometry from the shared map-slots query, so it never fetches.
 *
 * @return {JSX.Element | null} The editor, or null when nothing is grippable.
 */
export const TcLateralTrimLayer: FC = () => {
  const slotIds = useAtomValue(selectedLtdSlotIdsAtom);
  const { laterals, setManyCoords } = useLtdLaterals([...slotIds]);
  const editable = laterals.filter(
    (l) => l.valued !== false && l.fullCoords && l.currentCoords
  );

  // Dragging one end moves ONLY that end (to the cursor) and holds the other end
  // exactly where it is — no snap back to the full-length end / the unit edge. For
  // a multi-lateral selection the same feet-moved delta is applied off that end to
  // each, so the group keeps its relative trims. Distances run along the longest
  // line each lateral may reach (`maxCoords`), so an end can also be pulled out.
  const handleDrag = useCallback(
    (dragged: LtdLateral, end: 'low' | 'high', event: MarkerDragEvent) => {
      const full = dragged.maxCoords;
      const current = dragged.currentCoords;
      if (!full || !current) return;
      const cursor: GeoJSON.Position = [event.lngLat.lng, event.lngLat.lat];
      const cursorFt = projectCursorToLengthFromStart(full, cursor);
      // The dragged end's current position along the full line, and how far the
      // cursor moves it (signed, along the full axis).
      const draggedEnd =
        end === 'high' ? current[current.length - 1] : current[0];
      const deltaFt =
        cursorFt - projectCursorToLengthFromStart(full, draggedEnd);
      setManyCoords(
        editable.map((l) => {
          const f = l.maxCoords as GeoJSON.Position[];
          const c = l.currentCoords as GeoJSON.Position[];
          const fullLen = lateralLengthFt(f);
          const lowFt = projectCursorToLengthFromStart(f, c[0]);
          const highFt = projectCursorToLengthFromStart(f, c[c.length - 1]);
          if (end === 'high') {
            // Move the high end, hold the low end at its current position.
            const nextHigh = Math.max(
              lowFt + MIN_LTD_LEN_FT,
              Math.min(fullLen, highFt + deltaFt)
            );
            return [l.slotId, sliceLateralByLength(f, lowFt, nextHigh)];
          }
          // Move the low end, hold the high end at its current position.
          const nextLow = Math.max(
            0,
            Math.min(highFt - MIN_LTD_LEN_FT, lowFt + deltaFt)
          );
          return [l.slotId, sliceLateralByLength(f, nextLow, highFt)];
        })
      );
    },
    [editable, setManyCoords]
  );

  const restore = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setManyCoords(
        editable.map((l) => [l.slotId, l.fullCoords as GeoJSON.Position[]])
      );
    },
    [editable, setManyCoords]
  );

  if (editable.length === 0) return null;

  return (
    <>
      {editable.map((l) => {
        const coords = l.currentCoords as GeoJSON.Position[];
        if (coords.length < 2) return null;
        const low = coords[0];
        const high = coords[coords.length - 1];
        const line: GeoJSON.Feature = {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: {},
        };
        return (
          <Fragment key={l.slotId}>
            <Source
              id={`tc-lateral-trim-${l.slotId}`}
              type="geojson"
              data={line}
            >
              {/* Selection = the base LTD teal in a darker tone, thicker and
                  dashed — the same lateral, emphasised. Shared with the Add
                  Wellbore picker so the selected/unselected relationship reads
                  the same there; the reasoning lives with the constant. */}
              <Layer
                id={`tc-lateral-trim-line-${l.slotId}`}
                type="line"
                paint={LTD_SLOT_SELECTED_LINE_PAINT}
              />
            </Source>
            <Marker
              longitude={low[0]}
              latitude={low[1]}
              anchor="center"
              draggable
              onDrag={(event) => handleDrag(l, 'low', event)}
            >
              <div
                className={style.ltdHandle}
                title="Drag to trim · right-click to restore full length"
                onContextMenu={restore}
              />
            </Marker>
            <Marker
              longitude={high[0]}
              latitude={high[1]}
              anchor="center"
              draggable
              onDrag={(event) => handleDrag(l, 'high', event)}
            >
              <div
                className={style.ltdHandle}
                title="Drag to trim · right-click to restore full length"
                onContextMenu={restore}
              />
            </Marker>
          </Fragment>
        );
      })}
    </>
  );
};

TcLateralTrimLayer.displayName = 'TcLateralTrimLayer';
